import {gql} from '@apollo/client';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BenchlingState} from 'redux-stores/reduceApp';
import EntityStore from 'stores/EntityStore';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarFrameType} from 'ui-library/avatar/AvatarFrame';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import PartyAvatarList from 'ui-library/PartyAvatarList';

import {UsersByIds, UsersByIdsVariables} from './__generated/UsersByIds';

type UserNode = ExtractByTypename<UsersByIds['users'][number], 'UserNode'>;

export const USERS_BY_IDS_QUERY = gql`
  query UsersByIds($userIds: [StrictString!]!) @skipAudit @engComponent(name: CORE_OBJECT) {
    users(ids: $userIds) {
      ...UserAvatarInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('UserAvatarInfo').fragment}
`;

const DEFAULT_MAX_AVATARS = 5;

interface CachedAvatarList {
  resourceId: string;
  avatars: Array<HasAvatar & {id: string}>;
}

export default function ResourcePresenceView({resourceId}: {resourceId: string}): JSX.Element | null {
  const [cachedAvatarList, setCachedAvatarList] = useState<CachedAvatarList>({resourceId, avatars: []});
  if (cachedAvatarList.resourceId !== resourceId) {
    setCachedAvatarList({resourceId, avatars: []});
  }

  const presenceConnections = useSelector(
    (state: BenchlingState) => state.ui.presence[resourceId]?.presentClients ?? []
  );
  const activeEditorLock = useSelector(
    (state: BenchlingState) => state.ui.documentEditorLockState[resourceId] ?? null
  );
  const userIds = [...new Set(presenceConnections.map((val) => val.userId))];
  if (activeEditorLock && !userIds.includes(activeEditorLock.userId)) {
    userIds.push(activeEditorLock.userId);
  }

  const currentUserId: string = EntityStore.currentUser() ? EntityStore.currentUser().id : '';
  const avatarSortFunc = (a: HasAvatar & {id: string}, b: HasAvatar & {id: string}): number => {
    if (activeEditorLock) {
      if (a.id === activeEditorLock.userId) {
        return -1;
      } else if (b.id === activeEditorLock.userId) {
        return 1;
      }
    }

    if (a.id === currentUserId) {
      return -1;
    } else if (b.id === currentUserId) {
      return 1;
    } else {
      return a.id.localeCompare(b.id);
    }
  };

  const result = useBenchlingQuery<UsersByIds, UsersByIdsVariables>(USERS_BY_IDS_QUERY, {
    variables: {userIds},
  });
  const frames = activeEditorLock ? {[activeEditorLock.userId]: AvatarFrameType.EDITOR} : {};

  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return (
      <PartyAvatarList
        parties={cachedAvatarList.avatars}
        maxAvatars={DEFAULT_MAX_AVATARS}
        size={AvatarSize.SMALL}
        tooltipPlacement={FloatingPlacement.BOTTOM}
        framesByPartyId={frames}
      />
    );
  } else {
    const newValue = result.data.users
      .filter((user): user is UserNode => !isInaccessible(user))
      .map((user: UserNode): HasAvatar & {id: string} => ({
        id: user.id,
        name: user.name,
        avatarUrl: user.avatarUrl,
        email: user.email,
        handle: user.handle,
      }))
      .sort(avatarSortFunc);
    if (hasPresenceListChanged(newValue, cachedAvatarList.avatars)) {
      setCachedAvatarList({resourceId, avatars: newValue});
    }

    return (
      <PartyAvatarList
        parties={newValue}
        maxAvatars={DEFAULT_MAX_AVATARS}
        size={AvatarSize.SMALL}
        tooltipPlacement={FloatingPlacement.BOTTOM}
        framesByPartyId={frames}
      />
    );
  }
}

/**
 * Deeply compares a new presence list value with the previously cached value and returns true it there has
 * been any change.
 */
function hasPresenceListChanged(
  a: Array<HasAvatar & {id: string}>,
  b: Array<HasAvatar & {id: string}>
): boolean {
  if (a === b) {
    return false;
  }
  if (!a || !b || a.length !== b.length) {
    return true;
  }

  let result = false;
  a.forEach((val, idx) => {
    if (!avatarDataIsEqual(val, b[idx])) {
      result = true;
    }
  });
  return result;
}

function avatarDataIsEqual(a: HasAvatar & {id: string}, b: HasAvatar & {id: string}): boolean {
  return (
    a.id === b.id &&
    a.name === b.name &&
    a.handle === b.handle &&
    a.email === b.email &&
    a.avatarUrl === b.avatarUrl &&
    a.avatar_url === b.avatar_url
  );
}

export const _testing = {
  hasPresenceListChanged,
};
